import React, { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Plus, Pencil, Trash2, Loader2, Shirt, X } from "lucide-react";
import { toast } from "sonner";

const EMPTY = { label: "", options: [] };

export default function UniformCategories() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [optionDraft, setOptionDraft] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/uniform-categories");
      setItems(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load categories");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => { setEditing(null); setForm(EMPTY); setOptionDraft(""); setOpen(true); };
  const openEdit = (c) => {
    setEditing(c);
    setForm({ label: c.label, options: c.options || [] });
    setOptionDraft("");
    setOpen(true);
  };

  const addOption = () => {
    const s = optionDraft.trim();
    if (!s) return;
    if (form.options.some((o) => o.toLowerCase() === s.toLowerCase())) {
      toast.info("Already in the list");
      return;
    }
    setForm((f) => ({ ...f, options: [...f.options, s] }));
    setOptionDraft("");
  };
  const removeOption = (idx) => setForm((f) => ({ ...f, options: f.options.filter((_, i) => i !== idx) }));

  const submit = async () => {
    if (!form.label.trim()) { toast.error("Category name is required"); return; }
    setBusy(true);
    try {
      if (editing) {
        await api.put(`/uniform-categories/${editing.id}`, form);
        toast.success("Category updated");
      } else {
        await api.post("/uniform-categories", form);
        toast.success("Category added");
      }
      setOpen(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (c) => {
    if (!window.confirm(`Delete "${c.label}"? Existing employees' values for this category will also be cleared.`)) return;
    try {
      await api.delete(`/uniform-categories/${c.id}`);
      toast.success("Deleted");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    }
  };

  if (loading) return <div className="flex items-center justify-center py-20 text-zinc-400"><Loader2 className="w-5 h-5 animate-spin" /></div>;

  return (
    <div className="space-y-5 max-w-4xl">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <span className="label-overline">Setup</span>
          <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tighter mt-1">Uniform Categories</h1>
          <p className="text-sm text-zinc-500 mt-2 max-w-2xl">
            Each category here becomes a field on every employee's <em>My Profile → Uniform sizes</em>. Add a category like "Cap" or "Apron" if your property issues those — employees can then save their size.
          </p>
        </div>
        <Button onClick={openCreate} className="bg-zinc-950 hover:bg-zinc-800" data-testid="uniform-category-new">
          <Plus className="w-4 h-4 mr-1" /> New category
        </Button>
      </div>

      <div className="bg-white border border-zinc-200 rounded-md overflow-x-auto">
        <table className="w-full text-sm min-w-[640px]">
          <thead className="bg-zinc-50 border-b border-zinc-200">
            <tr className="text-left text-zinc-500 uppercase text-[10px] tracking-widest">
              <th className="py-2 px-4">Category</th>
              <th className="py-2 px-3">Size options</th>
              <th className="py-2 px-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && (
              <tr><td colSpan={3} className="py-12 text-center text-zinc-400">
                <Shirt className="w-6 h-6 mx-auto mb-2" /> No categories yet — add one to get started.
              </td></tr>
            )}
            {items.map((c) => (
              <tr key={c.id} className="border-b border-zinc-100 last:border-0" data-testid={`uniform-category-${c.key}`}>
                <td className="py-2.5 px-4">
                  <div className="font-medium">{c.label}</div>
                  <div className="text-[10px] text-zinc-500 font-mono">{c.key}{c.is_default ? " · default" : ""}</div>
                </td>
                <td className="py-2.5 px-3">
                  <div className="flex flex-wrap gap-1 max-w-md">
                    {(c.options || []).length === 0 ? (
                      <span className="text-zinc-400 italic text-xs">Free text — employee enters their own</span>
                    ) : (
                      (c.options || []).map((o) => (
                        <span key={o} className="text-[11px] bg-zinc-100 rounded px-1.5 py-0.5 font-mono">{o}</span>
                      ))
                    )}
                  </div>
                </td>
                <td className="py-2.5 px-3 text-right">
                  <button onClick={() => openEdit(c)} className="p-1.5 hover:bg-zinc-100 rounded" title="Edit" data-testid={`uniform-category-edit-${c.key}`}>
                    <Pencil className="w-3.5 h-3.5" />
                  </button>
                  {!c.is_default && (
                    <button onClick={() => remove(c)} className="p-1.5 hover:bg-red-50 hover:text-red-600 rounded ml-1" title="Delete" data-testid={`uniform-category-delete-${c.key}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md" data-testid="uniform-category-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? "Edit category" : "New uniform category"}</DialogTitle>
            <DialogDescription>
              Add the sizes you stock so employees can pick from a dropdown. Leave the options empty for a free-text field.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Category name</Label>
              <Input
                value={form.label}
                onChange={(e) => setForm({ ...form, label: e.target.value })}
                placeholder="e.g. Cap, Apron, Jacket"
                data-testid="uniform-category-label"
              />
            </div>
            <div>
              <Label className="text-xs">Size options</Label>
              <div className="flex gap-2 mt-1">
                <Input
                  value={optionDraft}
                  onChange={(e) => setOptionDraft(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addOption(); } }}
                  placeholder="e.g. M, 32, Free size"
                  data-testid="uniform-category-option-input"
                />
                <Button type="button" variant="outline" onClick={addOption} data-testid="uniform-category-option-add">
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
              {form.options.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mt-2">
                  {form.options.map((o, i) => (
                    <span key={`${o}-${i}`} className="inline-flex items-center gap-1 text-[11px] bg-zinc-100 rounded px-2 py-1 font-mono">
                      {o}
                      <button onClick={() => removeOption(i)} className="text-zinc-400 hover:text-red-600" aria-label={`Remove ${o}`}>
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} disabled={busy}>Cancel</Button>
            <Button onClick={submit} disabled={busy} className="bg-zinc-950 hover:bg-zinc-800" data-testid="uniform-category-save">
              {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : null}
              {editing ? "Save changes" : "Create category"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
